<script setup lang="ts">
import { computed, ref } from "vue";
import { formatMetricValue } from "@/composables/useKpiMetric";

const props = defineProps({
  // [{ id, label, color, unit, points: [{ value, n }|null] }]
  series: { type: Array, default: () => [] },
  // X axis labels, one per point index
  categories: { type: Array, default: () => [] },
  type: { type: String, default: "line" }, // line | area | bar
  // Overrides the unit-based formatting (value labels, tooltip).
  format: { type: Function, default: null },
  // Axis tick formatting when it should differ from values (e.g. "€10" vs "€10.0").
  tickFormat: { type: Function, default: null },
  // Value labels on every point get noisy past a few series: callers can turn them off.
  showValues: { type: Boolean, default: true },
});

const fmt = (value: number, unit?: string) => (props.format ? props.format(value) : formatMetricValue(value, unit as never));

const W = 720;
const H = 300;
const PAD = { left: 48, right: 16, top: 24, bottom: 44 };

const innerW = W - PAD.left - PAD.right;
const innerH = H - PAD.top - PAD.bottom;

// "Nice" number for axis ticks (Heckbert's algorithm).
function niceNum(range, round) {
  if (range <= 0) return 1;
  const exp = Math.floor(Math.log10(range));
  const f = range / 10 ** exp;
  let nf;
  if (round) {
    nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  } else {
    nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  }
  return nf * 10 ** exp;
}

// Axis scale: anchored at 0 (or below for negative values like NPS), rounded ticks.
const scale = computed(() => {
  const vals = props.series.flatMap((s) => s.points).filter((p) => p != null).map((p) => p.value);
  if (!vals.length) return { min: 0, max: 1, step: 1, ticks: [0, 1] };
  const dataMax = Math.max(...vals, 0);
  const dataMin = Math.min(...vals, 0);
  const targetTicks = 5;
  const step = niceNum(niceNum(dataMax - dataMin || 1, false) / (targetTicks - 1), true);
  const niceMin = Math.floor(dataMin / step) * step;
  const niceMax = Math.ceil(dataMax / step) * step;
  const ticks = [];
  for (let v = niceMin; v <= niceMax + step * 0.5; v += step) ticks.push(Number(v.toFixed(6)));
  return { min: niceMin, max: niceMax, step, ticks };
});

const count = computed(() => props.categories.length);

function xAt(i) {
  if (count.value <= 1) return PAD.left + innerW / 2;
  return PAD.left + (innerW * i) / (count.value - 1);
}

function yAt(v) {
  const { min, max } = scale.value;
  const span = max - min || 1;
  return PAD.top + innerH - (innerH * (v - min)) / span;
}

const yTicks = computed(() =>
  scale.value.ticks.map((v) => ({
    v,
    y: yAt(v),
    label: (props.tickFormat ?? props.format)
      ? (props.tickFormat ?? props.format)(v)
      : Number.isInteger(scale.value.step)
        ? Math.round(v)
        : v.toFixed(1),
  })),
);

// Past ~10 categories the labels collide: keep every k-th one plus the last,
// and anchor the edge labels inwards so they stay inside the drawing.
const MAX_X_LABELS = 10;
const xTicks = computed(() => {
  const n = count.value;
  const step = Math.max(1, Math.ceil(n / MAX_X_LABELS));
  const shown = new Set();
  for (let i = 0; i < n; i += step) shown.add(i);
  const lastRegular = Math.floor((n - 1) / step) * step;
  if (n - 1 - lastRegular < step / 2) shown.delete(lastRegular);
  shown.add(n - 1);
  return props.categories
    .map((label, i) => ({ label, i, x: xAt(i), anchor: i === 0 ? "start" : i === n - 1 ? "end" : "middle" }))
    .filter((t) => shown.has(t.i));
});

// Build line segments, breaking on null points.
function segmentsFor(series) {
  const segments = [];
  let current = [];
  series.points.forEach((p, i) => {
    if (p == null) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push({ x: xAt(i), y: yAt(p.value), value: p.value, n: p.n, i });
    }
  });
  if (current.length) segments.push(current);
  return segments;
}

const lineSeries = computed(() =>
  props.series.map((s) => {
    const segments = segmentsFor(s);
    const dots = segments.flat();
    const polylines = segments.map((seg) => seg.map((p) => `${p.x},${p.y}`).join(" "));
    const baseline = yAt(scale.value.min);
    const areas = segments
      .filter((seg) => seg.length > 1)
      .map((seg) => `${seg[0].x},${baseline} ${seg.map((p) => `${p.x},${p.y}`).join(" ")} ${seg.at(-1).x},${baseline}`);
    return { ...s, polylines, dots, areas };
  }),
);

// Grouped bars per category.
const barSeries = computed(() => {
  const n = props.series.length || 1;
  const step = count.value > 1 ? innerW / (count.value - 1) : innerW;
  const groupW = Math.min(step * 0.7, 56);
  const barW = groupW / n;
  const baseline = yAt(scale.value.min);
  return props.series.map((s, sIdx) => ({
    ...s,
    bars: s.points
      .map((p, i) => {
        if (p == null) return null;
        const x = xAt(i) - groupW / 2 + barW * sIdx;
        const y = yAt(p.value);
        return { x, y, w: barW, h: Math.max(0, baseline - y), value: p.value, n: p.n, i };
      })
      .filter(Boolean),
  }));
});

const isBar = computed(() => props.type === "bar");
const isArea = computed(() => props.type === "area");

// Value labels as HTML overlays (so they match the question cards' 11px font
// exactly, instead of scaling with the SVG viewBox).
const valueLabels = computed(() => {
  const out: { key: string; left: string; top: string; text: string }[] = [];
  if (!props.showValues) return out;
  const sources = isBar.value ? barSeries.value : lineSeries.value;
  for (const s of sources) {
    const pts = isBar.value ? s.bars : s.dots;
    for (const p of pts) {
      const px = isBar.value ? p.x + p.w / 2 : p.x;
      out.push({
        key: `${s.id}-${p.i}`,
        left: `${(px / W) * 100}%`,
        top: `${(p.y / H) * 100}%`,
        text: fmt(p.value, s.unit),
      });
    }
  }
  return out;
});

// --- Hover tooltip (HTML overlay positioned over the SVG) ---
const hovered = ref(null);

function show(series, x, y, value, n, category) {
  hovered.value = {
    left: `${(x / W) * 100}%`,
    top: `${(y / H) * 100}%`,
    color: series.color,
    label: series.label,
    category,
    value: fmt(value, series.unit),
    n,
  };
}

function hide() {
  hovered.value = null;
}
</script>

<template>
  <div class="relative">
    <svg :viewBox="`0 0 ${W} ${H}`" class="w-full">
      <!-- gridlines + Y labels -->
      <g>
        <line
          v-for="t in yTicks"
          :key="`g${t.v}`"
          :x1="PAD.left"
          :x2="W - PAD.right"
          :y1="t.y"
          :y2="t.y"
          stroke="var(--border)"
          stroke-width="1" />
        <text
          v-for="t in yTicks"
          :key="`y${t.v}`"
          :x="PAD.left - 8"
          :y="t.y + 3"
          text-anchor="end"
          class="fill-muted-foreground"
          font-size="11">{{ t.label }}</text>
      </g>

      <!-- bars -->
      <template v-if="isBar">
        <g v-for="s in barSeries" :key="s.id">
          <rect
            v-for="b in s.bars"
            :key="`b${b.i}`"
            :x="b.x"
            :y="b.y"
            :width="b.w"
            :height="b.h"
            :fill="s.color"
            rx="1.5"
            class="cursor-pointer"
            @mouseenter="show(s, b.x + b.w / 2, b.y, b.value, b.n, categories[b.i])"
            @mouseleave="hide" />
        </g>
      </template>

      <!-- lines / areas -->
      <template v-else>
        <g v-for="s in lineSeries" :key="s.id">
          <template v-if="isArea">
            <polygon
              v-for="(a, i) in s.areas"
              :key="`a${i}`"
              :points="a"
              :fill="s.color"
              fill-opacity="0.12"
              stroke="none" />
          </template>
          <polyline
            v-for="(pl, i) in s.polylines"
            :key="`l${i}`"
            :points="pl"
            fill="none"
            :stroke="s.color"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round" />
          <circle
            v-for="p in s.dots"
            :key="`d${p.i}`"
            :cx="p.x"
            :cy="p.y"
            r="3.5"
            :fill="s.color" />
          <circle
            v-for="p in s.dots"
            :key="`h${p.i}`"
            :cx="p.x"
            :cy="p.y"
            r="12"
            fill="transparent"
            class="cursor-pointer"
            @mouseenter="show(s, p.x, p.y, p.value, p.n, categories[p.i])"
            @mouseleave="hide" />
        </g>
      </template>

      <!-- X labels -->
      <text
        v-for="t in xTicks"
        :key="`x${t.i}`"
        :x="t.x"
        :y="H - 16"
        :text-anchor="t.anchor"
        class="fill-muted-foreground"
        font-size="11">{{ t.label }}</text>
    </svg>

    <!-- Value labels (HTML overlay so the font matches the question cards) -->
    <div
      v-for="l in valueLabels"
      :key="l.key"
      class="pointer-events-none absolute -translate-x-1/2 -translate-y-full text-xs font-medium tabular-nums text-foreground"
      :style="{ left: l.left, top: l.top, marginTop: '-6px' }">{{ l.text }}</div>

    <div
      v-if="hovered"
      class="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border bg-background px-2 py-1.5 text-xs shadow-md"
      :style="{ left: hovered.left, top: hovered.top, marginTop: '-10px' }">
      <div class="font-medium text-foreground">{{ hovered.category }}</div>
      <div class="mt-0.5 flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: hovered.color }" />
        <span class="text-foreground">{{ hovered.value }}</span>
        <span v-if="hovered.n != null" class="text-muted-foreground">· n = {{ hovered.n }}</span>
      </div>
      <div v-if="hovered.label" class="text-muted-foreground">{{ hovered.label }}</div>
    </div>
  </div>
</template>
